import { useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useWallet } from '@solana/wallet-adapter-react'
import { track } from 'shared/analytics/track'
import { userVar } from 'shared/store/user'
import { withWalletChoice } from 'shared/store/wallet-choice'
import { showNotificationToast } from 'shared/utils/notify'
import {
  connectSelectedSolanaWallet,
  getWalletErrorMessage,
  isUserRejectedWalletError
} from '../utils/connect-solana-adapter'
import type { SolanaConnectorProvider } from '../utils/get-solana-connector-providers'

/**
 * Connects a wallet app for a signed-in user (FE-306): wallet-adapter only,
 * no signature. The app becomes the user's wallet choice, kept across
 * reloads; the wallet plays when it is linked (FE-309). Each attempt is
 * reported as `wallet_connect_started` (FE-321).
 */
export const useConnectWalletApp = () => {
  const { wallets, select } = useWallet()
  const user = useReactiveVar(userVar)
  const [isConnecting, setIsConnecting] = useState(false)

  // eslint-disable-next-line max-statements
  const connectApp = async (provider: SolanaConnectorProvider) => {
    if (isConnecting) return false

    track('wallet_connect_started', { wallet: provider.walletName })
    setIsConnecting(true)
    const connect = () =>
      connectSelectedSolanaWallet(wallets, provider.walletName, select)

    try {
      await (user ?
        withWalletChoice(user.id, provider.walletName, connect)
      : connect())
      return true
    } catch (error) {
      if (!isUserRejectedWalletError(error)) {
        showNotificationToast({
          type: 'error',
          title: 'Wallet was not connected',
          description: getWalletErrorMessage(error)
        })
      }
      return false
    } finally {
      setIsConnecting(false)
    }
  }

  return { connectApp, isConnecting }
}
